import type {
  OwnerActivityEnvironment,
  OwnerActivityFilters,
  OwnerActivityOutcome,
  OwnerActivityScope,
} from '../../../services/ownerConsoleService'
import type { OwnerRouteQuery } from '../ownerRouter'
import type { NlsKey } from '../../../nls/strings_nls'

export type OwnerActivityBuiltinView =
  | 'all'
  | 'mine-7d'
  | 'destructive-24h'
  | 'breakglass'
  | 'failed'
  | 'reverts'

export const OWNER_ACTIVITY_DESTRUCTIVE_KINDS = [
  'SUSPEND_WORKSPACE',
  'RESET_WORKSPACE_CONTENT',
  'RESET_GAMIFICATION_FOR_USER',
  'RESET_CHAT_FOR_WORKSPACE',
  'RESET_AI_HISTORY_FOR_USER',
  'RESET_DESIGN_HUB_STALE',
  'FORCE_SIGN_OUT_EVERYWHERE',
  'FORCE_PASSWORD_RESET',
  'TOGGLE_ACCOUNT_DISABLED',
] as const

export const OWNER_ACTIVITY_KINDS = [
  'GRANT_CREDITS',
  'GRANT_WORKSPACE_CREDITS',
  'GRANT_PERSONAL_CREDITS',
  'CHANGE_PLAN',
  'CHANGE_WORKSPACE_CURRENCY',
  'SUSPEND_WORKSPACE',
  'FORCE_USAGE_REFRESH',
  'RETRY_FAILED_JOB',
  'SET_MAINTENANCE_MODE',
  'RESET_WORKSPACE_CONTENT',
  'RESET_GAMIFICATION_FOR_USER',
  'RESET_CHAT_FOR_WORKSPACE',
  'RESET_AI_HISTORY_FOR_USER',
  'RESET_DESIGN_HUB_STALE',
  'FORCE_ONBOARDING_FOR_USER',
  'INSPECT_WORKSPACE',
  'CONTENT_BREAK_GLASS',
  'FORCE_SIGN_OUT_EVERYWHERE',
  'FORCE_PASSWORD_RESET',
  'TOGGLE_ACCOUNT_DISABLED',
  'CURRENCY_MIGRATION_AUTO_USD',
  'support.ticket.update',
  'support.restriction.update',
  'support.ticket.internal_note',
  'support.ticket.reply',
  'support.canned_reply.upsert',
  'support.canned_reply.delete',
  'support.ticket.merge',
  'support.macro.upsert',
  'support.macro.run',
  'REVERT',
  'AUDIT_EXPORTED',
  'cohort.upsert',
  'cohort.archive',
  'cohort.preview_members',
  'flag.update',
  'SET_PLAN_LIMITS',
] as const

export const OWNER_ACTIVITY_KIND_LABELS: Record<typeof OWNER_ACTIVITY_KINDS[number], NlsKey> = {
  GRANT_CREDITS: 'NLSOwnerActivityKind0',
  GRANT_WORKSPACE_CREDITS: 'NLSOwnerActivityKind1',
  GRANT_PERSONAL_CREDITS: 'NLSOwnerActivityKind2',
  CHANGE_PLAN: 'NLSOwnerActivityKind3',
  CHANGE_WORKSPACE_CURRENCY: 'NLSOwnerActivityKind4',
  SUSPEND_WORKSPACE: 'NLSOwnerActivityKind5',
  FORCE_USAGE_REFRESH: 'NLSOwnerActivityKind6',
  RETRY_FAILED_JOB: 'NLSOwnerActivityKind7',
  SET_MAINTENANCE_MODE: 'NLSOwnerActivityKind8',
  RESET_WORKSPACE_CONTENT: 'NLSOwnerActivityKind9',
  RESET_GAMIFICATION_FOR_USER: 'NLSOwnerActivityKind10',
  RESET_CHAT_FOR_WORKSPACE: 'NLSOwnerActivityKind11',
  RESET_AI_HISTORY_FOR_USER: 'NLSOwnerActivityKind12',
  RESET_DESIGN_HUB_STALE: 'NLSOwnerActivityKind13',
  FORCE_ONBOARDING_FOR_USER: 'NLSOwnerActivityKind14',
  INSPECT_WORKSPACE: 'NLSOwnerActivityKind15',
  CONTENT_BREAK_GLASS: 'NLSOwnerActivityKind16',
  FORCE_SIGN_OUT_EVERYWHERE: 'NLSOwnerActivityKind17',
  FORCE_PASSWORD_RESET: 'NLSOwnerActivityKind18',
  TOGGLE_ACCOUNT_DISABLED: 'NLSOwnerActivityKind19',
  CURRENCY_MIGRATION_AUTO_USD: 'NLSOwnerActivityKind20',
  'support.ticket.update': 'NLSOwnerActivityKind21',
  'support.restriction.update': 'NLSOwnerActivityKind22',
  'support.ticket.internal_note': 'NLSOwnerActivityKind23',
  'support.ticket.reply': 'NLSOwnerActivityKind24',
  'support.canned_reply.upsert': 'NLSOwnerActivityKind25',
  'support.canned_reply.delete': 'NLSOwnerActivityKind26',
  'support.ticket.merge': 'NLSOwnerActivityKind27',
  'support.macro.upsert': 'NLSOwnerActivityKind28',
  'support.macro.run': 'NLSOwnerActivityKind29',
  REVERT: 'NLSOwnerActivityKind30',
  AUDIT_EXPORTED: 'NLSOwnerActivityKind31',
  'cohort.upsert': 'NLSOwnerActivityKindCohortUpsert',
  'cohort.archive': 'NLSOwnerActivityKindCohortArchive',
  'cohort.preview_members': 'NLSOwnerActivityKindCohortPreviewMembers',
  'flag.update': 'NLSOwnerActivityKindFlagUpdate',
  SET_PLAN_LIMITS: 'NLSOwnerActivityKindSetPlanLimits',
}

export const activityKindLabelKey = (kind: string): NlsKey | undefined => {
  return OWNER_ACTIVITY_KIND_LABELS[kind as typeof OWNER_ACTIVITY_KINDS[number]]
}

const OUTCOMES = new Set<OwnerActivityOutcome>(['SUCCESS', 'FAILED', 'PREVIEW', 'UNKNOWN'])
const ENVIRONMENTS = new Set<OwnerActivityEnvironment>(['sit', 'dev', 'prod', 'unknown'])
const SCOPES = new Set<OwnerActivityScope>(['global', 'workspace', 'user', 'breakglass', 'unknown'])
const ACTIVITY_QUERY_KEYS = new Set(['activityView', 'from', 'to', 'actor', 'kinds', 'outcomes', 'scope', 'environments'])

const listValue = <T extends string>(value: string | undefined, label: string, allowed?: Set<T>): T[] | undefined => {
  if (!value) return undefined
  const values = [...new Set(value.split(',').map((entry) => entry.trim()).filter(Boolean))]
  if (!values.length || (allowed && values.some((entry) => !allowed.has(entry as T)))) {
    throw new Error(`Invalid Activity ${label} filter.`)
  }
  return values as T[]
}

const canonicalIso = (value: string | undefined) => {
  if (!value) return undefined
  const millis = Date.parse(value)
  if (!Number.isFinite(millis)) throw new Error('Invalid Activity date filter.')
  const canonical = new Date(millis).toISOString()
  if (canonical !== value) throw new Error('Invalid Activity date filter.')
  return canonical
}

export const parseOwnerActivityFilters = (query?: OwnerRouteQuery): OwnerActivityFilters => {
  if (query?.scope && !SCOPES.has(query.scope as OwnerActivityScope)) throw new Error('Invalid Activity scope filter.')
  if (query?.actor && query.actor !== query.actor.trim()) throw new Error('Invalid Activity actor filter.')
  const scope = query?.scope as OwnerActivityScope | undefined
  return {
    from: canonicalIso(query?.from),
    to: canonicalIso(query?.to),
    actor: query?.actor || undefined,
    kinds: listValue(query?.kinds, 'action kind'),
    outcomes: listValue(query?.outcomes, 'outcome', OUTCOMES),
    scope,
    environments: listValue(query?.environments, 'environment', ENVIRONMENTS),
  }
}

export const ownerActivityFiltersToQuery = (
  filters: OwnerActivityFilters,
  current?: OwnerRouteQuery,
  activityView?: OwnerActivityBuiltinView,
): OwnerRouteQuery => {
  const query: OwnerRouteQuery = {}
  Object.entries(current ?? {}).forEach(([key, value]) => {
    if (!ACTIVITY_QUERY_KEYS.has(key) && value) query[key] = value
  })
  if (activityView && activityView !== 'all') query.activityView = activityView
  if (filters.from) query.from = filters.from
  if (filters.to) query.to = filters.to
  if (filters.actor && filters.actor !== 'all') query.actor = filters.actor
  if (filters.kinds?.length) query.kinds = [...filters.kinds].sort().join(',')
  if (filters.outcomes?.length) query.outcomes = [...filters.outcomes].sort().join(',')
  if (filters.scope && filters.scope !== 'all') query.scope = filters.scope
  if (filters.environments?.length) query.environments = [...filters.environments].sort().join(',')
  return query
}

const isoBefore = (now: Date, millis: number) => new Date(now.getTime() - millis).toISOString()

export const filtersForOwnerActivityView = (
  view: OwnerActivityBuiltinView,
  now = new Date(),
): OwnerActivityFilters => {
  if (view === 'mine-7d') {
    return { actor: 'self', from: isoBefore(now, 7 * 24 * 60 * 60 * 1_000), to: now.toISOString() }
  }
  if (view === 'destructive-24h') {
    return { kinds: [...OWNER_ACTIVITY_DESTRUCTIVE_KINDS], from: isoBefore(now, 24 * 60 * 60 * 1_000), to: now.toISOString() }
  }
  if (view === 'breakglass') return { kinds: ['CONTENT_BREAK_GLASS'], scope: 'breakglass' }
  if (view === 'failed') return { outcomes: ['FAILED'] }
  if (view === 'reverts') return { kinds: ['REVERT'] }
  return {}
}

export const ownerActivityViewFromQuery = (query?: OwnerRouteQuery): OwnerActivityBuiltinView => {
  const view = query?.activityView
  return view === 'mine-7d' || view === 'destructive-24h' || view === 'breakglass' || view === 'failed' || view === 'reverts'
    ? view
    : 'all'
}

export const resolveOwnerActivityFilters = (query?: OwnerRouteQuery, now = new Date()): OwnerActivityFilters => {
  const explicit = parseOwnerActivityFilters(query)
  return {
    ...filtersForOwnerActivityView(ownerActivityViewFromQuery(query), now),
    ...Object.fromEntries(Object.entries(explicit).filter(([, value]) => value !== undefined)),
  }
}

export const activityDateInputValue = (iso?: string) => iso?.slice(0, 10) ?? ''

export const activityDateBoundary = (value: string, edge: 'from' | 'to') => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined
  const suffix = edge === 'from' ? 'T00:00:00.000Z' : 'T23:59:59.999Z'
  const iso = `${value}${suffix}`
  return Number.isFinite(Date.parse(iso)) && new Date(Date.parse(iso)).toISOString() === iso ? iso : undefined
}
